import {formatMoney} from '@shopify/theme-currency';
import FlickityFade from 'flickity-fade';

import QuantityCounter from '../globals/quantity-handle';
import hideElement from '../util/hide-element';
import showElement from '../util/show-element';
import {ProductForm} from '../vendor/theme-scripts/theme-product-form';

const selectors = {
  product: '[data-product]',
  productForm: '[data-product-form]',
  addToCart: '[data-add-to-cart]',
  addToCartText: '[data-add-to-cart-text]',
  comparePrice: '[data-compare-price]',
  comparePriceText: '[data-compare-text]',
  formWrapper: '[data-form-wrapper]',
  originalSelectorId: '[data-product-select]',
  priceWrapper: '[data-price-wrapper]',
  productSlideshow: '[data-product-slideshow]',
  productImage: '[data-product-image]',
  productJson: '[data-product-json]',
  productPrice: '[data-product-price]',
  unitPrice: '[data-product-unit-price]',
  unitBase: '[data-product-base]',
  unitWrapper: '[data-product-unit]',
  preOrderTag: '_preorder',
  sliderEnabled: 'flickity-enabled',
  productSlide: '.product__slide',
  dataTallLayout: 'data-tall-layout',
  dataEnableHistoryState: 'data-enable-history-state',
  subPrices: '[data-subscription-watch-price]',
  subSelectors: '[data-subscription-selectors]',
  subOffWrap: '[data-price-off]',
  subsToggle: '[data-toggles-group]',
  subsChild: 'data-group-toggle',
  subOffAmount: '[data-price-off-amount]',
  subDescription: '[data-plan-description]',
  dataImageId: 'data-image-id',
  idInput: '[name="id"]',
};

const classes = {
  hide: 'hide',
  variantSoldOut: 'variant--soldout',
  variantUnavailable: 'variant--unavailabe',
  productPriceSale: 'product__price--sale',
};

class ProductAddForm {
  constructor(section) {
    this.section = section;
    this.container = section.container;
    this.tallLayout = this.container.getAttribute(selectors.dataTallLayout) === 'true';
    this.product = this.container.querySelector(selectors.product);

    // Stop parsing if we don't have the product
    if (!this.product) {
      return;
    }

    this.productForm = this.container.querySelector(selectors.productForm);
    this.enableHistoryState = this.container.getAttribute(selectors.dataEnableHistoryState) === 'true';
    this.hasUnitPricing = this.container.querySelector(selectors.unitWrapper);
    this.subSelectors = this.container.querySelector(selectors.subSelectors);
    this.subPrices = this.container.querySelector(selectors.subPrices);

    const counter = new QuantityCounter(this.container);
    counter.init();

    this.init();
  }

  init() {
    let productJSON = null;
    const productElemJSON = this.container.querySelector(selectors.productJson);
    if (productElemJSON) {
      productJSON = productElemJSON.innerHTML;
    }
    if (productJSON) {
      this.productJSON = JSON.parse(productJSON);
      this.linkForm();
    } else {
      console.error('Missing product JSON');
    }
  }

  destroy() {
    this.productForm.destroy();
  }

  linkForm() {
    this.productForm = new ProductForm(this.productForm, this.productJSON, {
      onOptionChange: this.onOptionChange.bind(this),
      onPlanChange: this.onPlanChange.bind(this),
    });
    this.pushState(this.productForm.getFormState());
    this.subsToggleListeners();
  }

  onOptionChange(evt) {
    this.pushState(evt.dataset);
    this.updateProductImage(evt);
  }

  onPlanChange(evt) {
    if (this.subPrices) {
      this.pushState(evt.dataset);
    }
  }

  pushState(formState) {
    this.productState = this.setProductState(formState);
    this.updateAddToCartState(formState);
    this.updateProductPrices(formState);
    this.updateSubscriptionText(formState);
    if (this.enableHistoryState) {
      this.updateHistoryState(formState);
    }
  }

  updateAddToCartState(formState) {
    const variant = formState.variant;
    let addText = theme.strings.addToCart;
    const priceWrapper = this.container.querySelectorAll(selectors.priceWrapper);
    const addToCart = this.container.querySelectorAll(selectors.addToCart);
    const addToCartText = this.container.querySelectorAll(selectors.addToCartText);
    const formWrapper = this.container.querySelectorAll(selectors.formWrapper);

    if (this.productJSON.tags.includes(selectors.preOrderTag)) {
      addText = theme.strings.preOrder;
    }

    if (priceWrapper.length && variant) {
      priceWrapper.forEach((element) => {
        element.classList.remove(classes.hide);
      });
    }

    if (addToCart.length) {
      addToCart.forEach((element) => {
        if (variant) {
          if (variant.available) {
            element.disabled = false;
          } else {
            element.disabled = true;
          }
        } else {
          element.disabled = true;
        }
      });
    }

    if (addToCartText.length) {
      addToCartText.forEach((element) => {
        if (variant) {
          if (variant.available) {
            element.innerHTML = addText;
          } else {
            element.innerHTML = theme.strings.soldOut;
          }
        } else {
          element.innerHTML = theme.strings.unavailable;
        }
      });
    }

    if (formWrapper.length) {
      formWrapper.forEach((element) => {
        if (variant) {
          if (variant.available) {
            element.classList.remove(classes.variantSoldOut, classes.variantUnavailable);
          } else {
            element.classList.add(classes.variantSoldOut);
            element.classList.remove(classes.variantUnavailable);
          }
          const formSelect = element.querySelector(selectors.originalSelectorId);
          if (formSelect) {
            formSelect.value = variant.id;
          }
        } else {
          element.classList.add(classes.variantUnavailable);
          element.classList.remove(classes.variantSoldOut);
        }
      });
    }
  }

  updateHistoryState(formState) {
    const variant = formState.variant;
    const plan = formState.plan;
    const location = window.location.href;
    if (variant && location.includes('/product')) {
      const url = new window.URL(location);
      const params = url.searchParams;
      params.set('variant', variant.id);
      if (plan && plan.detail && plan.detail.id && this.productState.hasPlan) {
        params.set('selling_plan', plan.detail.id);
      } else {
        params.delete('selling_plan');
      }
      url.search = params.toString();
      const urlString = url.toString();
      window.history.replaceState({path: urlString}, '', urlString);
    }
  }

  getBaseUnit(variant) {
    return variant.unit_price_measurement.reference_value === 1
      ? variant.unit_price_measurement.reference_unit
      : variant.unit_price_measurement.reference_value + variant.unit_price_measurement.reference_unit;
  }

  subsToggleListeners() {
    const toggles = this.container.querySelectorAll(selectors.subsToggle);

    toggles.forEach((toggle) => {
      toggle.addEventListener(
        'change',
        function (e) {
          const val = e.target.value.toString();
          const selected = this.container.querySelector(`[${selectors.subsChild}="${val}"]`);
          const groups = this.container.querySelectorAll(`[${selectors.subsChild}]`);
          if (selected) {
            selected.classList.remove(classes.hide);
            const first = selected.querySelector(`[name="selling_plan"]`);
            first.checked = true;
            first.dispatchEvent(new Event('change'));
          }
          groups.forEach((group) => {
            if (group !== selected) {
              group.classList.add(classes.hide);
              const plans = group.querySelectorAll(`[name="selling_plan"]`);
              plans.forEach((plan) => {
                plan.checked = false;
                plan.dispatchEvent(new Event('change'));
              });
            }
          });
        }.bind(this)
      );
    });
  }

  updateSubscriptionText(formState) {
    const plan = formState.plan;
    const subOffWrap = this.container.querySelector(selectors.subOffWrap);
    const subOffAmount = this.container.querySelector(selectors.subOffAmount);
    const planDecription = this.container.querySelector(selectors.subDescription);

    if (this.productState.planSale) {
      const adjustment = plan.detail.price_adjustments[0];
      const discount = adjustment.value;
      if (adjustment && adjustment.value_type === 'percentage') {
        subOffAmount.innerHTML = `${discount}%`;
      } else {
        subOffAmount.innerHTML = formatMoney(discount, theme.moneyFormat);
      }
      subOffWrap.classList.remove(classes.hide);
    } else {
      subOffWrap.classList.add(classes.hide);
    }
    if (plan) {
      planDecription.innerHTML = plan.detail.description;
      planDecription.classList.remove(classes.hide);
    } else if (planDecription) {
      planDecription.classList.add(classes.hide);
    }
  }

  updateProductPrices(formState) {
    const variant = formState.variant;
    const plan = formState.plan;
    const priceWrappers = this.container.querySelectorAll(selectors.priceWrapper);

    priceWrappers.forEach((wrap) => {
      const comparePriceEl = wrap.querySelector(selectors.comparePrice);
      const productPriceEl = wrap.querySelector(selectors.productPrice);
      const comparePriceText = wrap.querySelector(selectors.comparePriceText);

      let comparePrice = '';
      let price = '';

      if (this.productState.available) {
        comparePrice = variant.compare_at_price;
        price = variant.price;
      }

      if (this.productState.hasPlan) {
        price = plan.allocation.price;
      }

      if (this.productState.planSale) {
        comparePrice = plan.allocation.compare_at_price;
        price = plan.allocation.price;
      }

      if (comparePriceEl) {
        if (this.productState.onSale || this.productState.planSale) {
          comparePriceEl.classList.remove(classes.hide);
          comparePriceText.classList.remove(classes.hide);
          productPriceEl.classList.add(classes.productPriceSale);
        } else {
          comparePriceEl.classList.add(classes.hide);
          comparePriceText.classList.add(classes.hide);
          productPriceEl.classList.remove(classes.productPriceSale);
        }
        comparePriceEl.innerHTML = formatMoney(comparePrice, theme.moneyFormat);
      }

      productPriceEl.innerHTML = formatMoney(price, theme.moneyFormat);
    });

    if (this.hasUnitPricing) {
      this.updateProductUnits(formState);
    }
  }

  updateProductUnits(formState) {
    const variant = formState.variant;
    const plan = formState.plan;
    let unitPrice = null;

    if (variant && variant.unit_price) {
      unitPrice = variant.unit_price;
    }
    if (plan && plan.allocation && plan.allocation.unit_price) {
      unitPrice = plan.allocation.unit_price;
    }

    if (unitPrice) {
      const base = this.getBaseUnit(variant);
      const formattedPrice = formatMoney(unitPrice, theme.moneyFormat);
      this.container.querySelector(selectors.unitPrice).innerHTML = formattedPrice;
      this.container.querySelector(selectors.unitBase).innerHTML = base;
      showElement(this.container.querySelector(selectors.unitWrapper));
    } else {
      hideElement(this.container.querySelector(selectors.unitWrapper));
    }
  }

  /**
   * Tracks aspects of the product state that are relevant to UI updates
   * @param {object} evt - variant change event
   * @return {object} productState - represents state of variant + plans
   *  productState.available - current variant and selling plan options result in valid offer
   *  productState.soldOut - variant is sold out
   *  productState.onSale - variant is on sale
   *  productState.showUnitPrice - variant has unit price
   *  productState.requiresPlan - all the product variants requires a selling plan
   *  productState.hasPlan - there is a valid selling plan
   *  productState.planSale - plan has a discount to show next to price
   *  productState.planPerDelivery - plan price does not equal per_delivery_price - a prepaid subscribtion
   */
  setProductState(dataset) {
    const variant = dataset.variant;
    const plan = dataset.plan;

    const productState = {
      available: true,
      soldOut: false,
      onSale: false,
      showUnitPrice: false,
      requiresPlan: false,
      hasPlan: false,
      planPerDelivery: false,
      planSale: false,
    };

    if (!variant || (variant.requires_selling_plan && !plan)) {
      productState.available = false;
    } else {
      if (!variant.available) {
        productState.soldOut = true;
      }

      if (variant.compare_at_price > variant.price) {
        productState.onSale = true;
      }

      if (variant.unit_price) {
        productState.showUnitPrice = true;
      }

      if (this.product && this.product.requires_selling_plan) {
        productState.requiresPlan = true;
      }

      if (plan && this.subPrices) {
        productState.hasPlan = true;
        if (plan.allocation.per_delivery_price !== plan.allocation.price) {
          productState.planPerDelivery = true;
        }
        if (variant.price > plan.allocation.price) {
          productState.planSale = true;
        }
      }
    }
    return productState;
  }

  updateProductImage(evt) {
    const variant = evt.dataset.variant;

    if (variant) {
      // Update variant image, if one is set
      if (variant.featured_media) {
        const newImg = this.container.querySelector(`${selectors.productImage}[${selectors.dataImageId}="${variant.featured_media.id}"]`);
        const newImageParent = newImg.closest(selectors.productSlide);
        // If we have a mobile breakpoint or the tall layout is disabled,
        // just switch the slideshow.
        if (newImageParent) {
          const newImagePos = Array.from(newImageParent.parentElement.children).indexOf(newImageParent);
          const slider = this.container.querySelector(selectors.productSlideshow);

          if (slider && slider.classList.contains(selectors.sliderEnabled)) {
            FlickityFade.data(slider).select(newImagePos);
          }

          if (!theme.variables.bpSmall && this.tallLayout) {
            // We know its a tall layout, if it's sticky
            // scroll to the images
            // Scroll to/reorder image unless it's the first photo on load
            const targetScroll = newImg.getBoundingClientRect().top + window.scrollY;

            if (newImagePos === 0 && targetScroll > window.pageYOffset) return;

            // Scroll to variant image
            document.dispatchEvent(new CustomEvent('poppy:close'));
            window.scrollTo({
              top: targetScroll,
              left: 0,
              behavior: 'smooth',
            });
          }
        }
      }
    }
  }
}

const productFormSection = {
  onLoad() {
    this.section = new ProductAddForm(this);
  },
};

export {ProductAddForm, productFormSection};
